// Offline outbox: messages that couldn't reach the server are kept in localStorage and re-sent (idempotently via client_id) when back online.
import { api } from "./api";
import { analytics } from "./analytics";

const KEY = "tc_outbox_v1";
const listeners = new Set();

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = (items) => { localStorage.setItem(KEY, JSON.stringify(items)); listeners.forEach((fn) => fn(items)); };

export const outbox = {
  all: read,
  count: () => read().length,
  subscribe(fn) { listeners.add(fn); fn(read()); return () => listeners.delete(fn); },
  add(item) { write([...read().filter((x) => x.client_id !== item.client_id), { ...item, queued_at: Date.now(), attempts: 0 }]); },
  remove(clientId) { write(read().filter((x) => x.client_id !== clientId)); },
  forUser(userId) { return read().filter((x) => x.user_id === userId); },
};

export const isNetworkError = (e) => !e?.response || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED" || [502, 503, 504].includes(e?.response?.status);

let flushing = false;
export const flushOutbox = async ({ userId, onSent, onFailed }) => {
  if (flushing || (typeof navigator !== "undefined" && navigator.onLine === false)) return 0;
  flushing = true;
  let sent = 0;
  try {
    for (const item of outbox.forUser(userId)) {
      try {
        const { data } = await api.post(`/conversations/${item.conversation_id}/messages`, { text: item.text, parent_id: item.parent_id || null, client_id: item.client_id, file_ids: item.file_ids || [] });
        outbox.remove(item.client_id);
        sent += 1;
        onSent?.(data, item);
        analytics.track("outbox_flushed", { category: "reliability", conversation_id: item.conversation_id, metadata: { waited_ms: Date.now() - item.queued_at, attempts: item.attempts + 1 } });
      } catch (e) {
        if (isNetworkError(e)) {
          write(read().map((x) => (x.client_id === item.client_id ? { ...x, attempts: (x.attempts || 0) + 1 } : x)));
          break; // still offline — try the rest later
        }
        outbox.remove(item.client_id); // permanent failure (403/400) — drop and tell the user
        onFailed?.(e, item);
        analytics.track("outbox_dropped", { category: "reliability", conversation_id: item.conversation_id, metadata: { status: e?.response?.status } });
      }
    }
  } finally { flushing = false; }
  return sent;
};
